<template>
  <div class="image-magnifier">
    <!-- 主图显示区域 -->
    <div class="main-image-container">
      <div
        class="main-image"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
        @mouseenter="handleMouseEnter"
        ref="mainImageContainer"
      >
        <img
          :src="currentImage"
          :alt="alt"
          ref="mainImage"
        />
        <!-- 放大镜遮罩 -->
        <div
          class="magnifier-mask"
          v-show="showMask"
          :style="maskStyle"
        ></div>
      </div>

      <!-- 放大显示区域 -->
      <div
        class="magnified-view"
        v-show="showMagnifier"
        ref="magnifiedView"
      >
        <img
          :src="currentImage"
          :alt="alt"
          :style="magnifiedImageStyle"
          ref="magnifiedImage"
        />
      </div>
    </div>

    <!-- 缩略图轮播 -->
    <div class="thumbnail-carousel" v-if="images.length > 0">
      <div class="carousel-container">
        <button
          class="carousel-btn prev"
          @click="scrollThumbnails('prev')"
          :disabled="scrollPosition === 0"
        >
          <i class="el-icon-arrow-left"></i>
        </button>

        <div class="thumbnails-wrapper" ref="thumbnailsWrapper">
          <div class="thumbnails" :style="thumbnailsStyle">
            <div
              v-for="(image, index) in images"
              :key="index"
              class="thumbnail"
              :class="{ active: currentImageIndex === index }"
              @click="selectImage(index)"
            >
              <img :src="image" :alt="`缩略图 ${index + 1}`" />
            </div>
          </div>
        </div>

        <button
          class="carousel-btn next"
          @click="scrollThumbnails('next')"
          :disabled="scrollPosition >= maxScrollPosition"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageMagnifier',
  props: {
    images: {
      type: Array,
      required: true,
      default: () => []
    },
    alt: {
      type: String,
      default: '产品图片'
    },
    magnificationLevel: {
      type: Number,
      default: 3.5
    }
  },
  data() {
    return {
      currentImageIndex: 0,
      showMask: false,
      showMagnifier: false,
      maskPosition: { x: 0, y: 0 },
      scrollPosition: 0,
      thumbnailWidth: 80,
      visibleThumbnails: 5,
      isImageReady: false
    }
  },
  computed: {
    currentImage() {
      return this.images[this.currentImageIndex] || this.images[0]
    },
    maskStyle() {
      return {
        left: this.maskPosition.x + 'px',
        top: this.maskPosition.y + 'px'
      }
    },
    magnifiedImageStyle() {
      if (!this.showMask || !this.isImageReady || !this.$refs.mainImage || !this.$refs.mainImageContainer) {
        return { display: 'none' }
      }

      const scale = this.magnificationLevel
      const maskSize = 120

      // 获取图片元素
      const img = this.$refs.mainImage
      const container = this.$refs.mainImageContainer

      // 等待图片加载
      if (!img.complete || img.naturalWidth === 0) {
        return { display: 'none' }
      }

      try {
        const containerRect = container.getBoundingClientRect()
        const imgRect = img.getBoundingClientRect()

        // 计算图片相对于容器的偏移
        const imgOffsetX = imgRect.left - containerRect.left
        const imgOffsetY = imgRect.top - containerRect.top

        // 计算遮罩中心相对于图片的位置
        const maskCenterX = this.maskPosition.x + maskSize / 2 - imgOffsetX
        const maskCenterY = this.maskPosition.y + maskSize / 2 - imgOffsetY

        // 计算相对位置 (0-1)
        const relativeX = maskCenterX / imgRect.width
        const relativeY = maskCenterY / imgRect.height

        // 放大后的图片尺寸
        const scaledWidth = imgRect.width * scale
        const scaledHeight = imgRect.height * scale

        // 计算偏移量，使遮罩中心对应的区域显示在放大视图中心
        const viewCenterX = 240 // 480/2
        const viewCenterY = 240 // 480/2

        const offsetX = viewCenterX - (relativeX * scaledWidth)
        const offsetY = viewCenterY - (relativeY * scaledHeight)

        return {
          width: scaledWidth + 'px',
          height: scaledHeight + 'px',
          transform: `translate(${offsetX}px, ${offsetY}px)`,
          transformOrigin: 'top left'
        }
      } catch (error) {
        return { display: 'none' }
      }
    },
    thumbnailsStyle() {
      return {
        transform: `translateX(-${this.scrollPosition * (this.thumbnailWidth + 10)}px)`
      }
    },
    maxScrollPosition() {
      return Math.max(0, this.images.length - this.visibleThumbnails)
    }
  },
  watch: {
    currentImage() {
      // 当图片切换时重置状态
      this.isImageReady = false
      this.showMask = false
      this.$nextTick(() => {
        this.initializeMagnifier()
      })
    }
  },
  mounted() {
    this.calculateVisibleThumbnails()
    window.addEventListener('resize', this.calculateVisibleThumbnails)
    // 等待DOM渲染完成后再初始化
    this.$nextTick(() => {
      this.initializeMagnifier()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateVisibleThumbnails)
  },
  methods: {
    selectImage(index) {
      this.currentImageIndex = index
      // 重置遮罩位置和状态
      this.showMask = false
      this.$nextTick(() => {
        this.initializeMagnifier()
      })
    },
    initializeMagnifier() {
      // 确保所有必要的DOM元素都已渲染
      if (this.$refs.mainImage && this.$refs.magnifiedView && this.$refs.mainImageContainer) {
        // 图片加载完成后再进行初始化
        const img = this.$refs.mainImage
        if (img.complete) {
          this.setupMagnifier()
        } else {
          img.onload = () => {
            this.setupMagnifier()
          }
        }
      }
    },
    setupMagnifier() {
      // 放大镜初始化完成，设置图片准备状态
      this.isImageReady = true
    },
    handleMouseEnter() {
      this.showMagnifier = true
      // showMask 将在 handleMouseMove 中根据鼠标位置动态设置
    },
    handleMouseLeave() {
      this.showMask = false
      this.showMagnifier = false
    },
    handleMouseMove(event) {
      if (!this.showMagnifier || !this.$refs.mainImageContainer || !this.$refs.mainImage) return

      const container = this.$refs.mainImageContainer
      const img = this.$refs.mainImage
      const maskSize = 120

      // 确保图片已加载
      if (!img.complete) return

      const containerRect = container.getBoundingClientRect()
      const imgRect = img.getBoundingClientRect()

      // 计算鼠标相对于容器的位置
      const mouseX = event.clientX - containerRect.left
      const mouseY = event.clientY - containerRect.top

      // 计算图片在容器中的位置
      const imgLeft = (containerRect.width - imgRect.width) / 2
      const imgTop = (containerRect.height - imgRect.height) / 2
      const imgRight = imgLeft + imgRect.width
      const imgBottom = imgTop + imgRect.height

      // 定义安全距离
      const safeDistance = 40

      // 只在鼠标位于图片安全区域内时显示遮罩
      if (mouseX >= imgLeft + safeDistance && mouseX <= imgRight - safeDistance &&
          mouseY >= imgTop + safeDistance && mouseY <= imgBottom - safeDistance) {
        // 计算遮罩位置（相对于容器）
        let x = mouseX - maskSize / 2
        let y = mouseY - maskSize / 2

        // 限制遮罩在图片安全区域内
        x = Math.max(imgLeft + safeDistance, Math.min(x, imgRight - maskSize - safeDistance))
        y = Math.max(imgTop + safeDistance, Math.min(y, imgBottom - maskSize - safeDistance))

        this.maskPosition = { x, y }
        this.showMask = true
      } else {
        this.showMask = false
      }
    },
    scrollThumbnails(direction) {
      if (direction === 'prev' && this.scrollPosition > 0) {
        this.scrollPosition--
      } else if (direction === 'next' && this.scrollPosition < this.maxScrollPosition) {
        this.scrollPosition++
      }
    },
    calculateVisibleThumbnails() {
      if (this.$refs.thumbnailsWrapper) {
        const wrapperWidth = this.$refs.thumbnailsWrapper.clientWidth
        this.visibleThumbnails = Math.floor(wrapperWidth / (this.thumbnailWidth + 10))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.image-magnifier {
  .main-image-container {
    position: relative;
    display: flex;
    gap: 20px;
    margin-bottom: 20px;

    .main-image {
      position: relative;
      flex: 1;
      max-width: 480px;
      min-height: 380px;
      background: #f8f9fa;
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      overflow: hidden;
      cursor: crosshair;
      display: flex;
      align-items: center;
      justify-content: center;
      //padding: 20px; // 添加安全边距，防止放大镜显示到边缘

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
      }

      .magnifier-mask {
        position: absolute;
        width: 120px;
        height: 120px;
        background: rgba(255, 255, 255, 0.4);
        border: 2px solid rgba(255, 255, 255, 0.8);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
        pointer-events: none;
        z-index: 10;
        cursor: crosshair;
      }
    }

    .magnified-view {
      position: absolute;
      left: 480px;
      top: 0;
      width: 480px;
      height: 480px;
      border: none;
      border-radius: 0;
      overflow: hidden;
      background: transparent;
      z-index: 20;
      box-shadow: none;

      img {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  .thumbnail-carousel {
    max-width: 480px; // 与主图最大宽度保持一致

    .carousel-container {
      display: flex;
      align-items: center;
      gap: 10px;

      .carousel-btn {
        width: 32px;
        height: 32px;
        border: 1px solid #ddd;
        background: #fff;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        flex-shrink: 0;

        &:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #0555b6;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        i {
          font-size: 14px;
          color: #666;
        }
      }

      .thumbnails-wrapper {
        flex: 1;
        overflow: hidden;

        .thumbnails {
          display: flex;
          gap: 10px;
          transition: transform 0.3s ease;

          .thumbnail {
            width: 60px;
            height: 60px;
            border: 2px solid transparent;
            border-radius: 4px;
            overflow: hidden;
            cursor: pointer;
            transition: border-color 0.3s ease;
            flex-shrink: 0;

            &:hover {
              border-color: #0555b6;
            }

            &.active {
              border-color: #0555b6;
            }

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .image-magnifier {
    .main-image-container {
      .magnified-view {
        left: 500px;
        width: 380px;
        height: 380px;
      }
    }
  }
}

@media (max-width: 1024px) {
  .image-magnifier {
    .main-image-container {
      flex-direction: column;

      .main-image {
        max-width: 100%;
      }

      .magnified-view {
        position: static;
        margin-top: 20px;
        width: 100%;
        height: 380px;
        left: auto;
        top: auto;
      }
    }

    .thumbnail-carousel {
      max-width: 100%; // 平板设备上与主图宽度一致
    }
  }
}

@media (max-width: 768px) {
  .image-magnifier {
    .main-image-container {
      .main-image {
        cursor: default;
        min-height: 300px;
      }

      .magnified-view {
        display: none; // 小屏幕完全隐藏放大镜
      }
    }

    .thumbnail-carousel {
      .carousel-container {
        .thumbnails-wrapper {
          .thumbnails {
            .thumbnail {
              width: 60px;
              height: 60px;
            }
          }
        }
      }
    }
  }
}
</style>
